import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useQueryState, parseAsInteger, parseAsString } from 'nuqs';
import { useCallback, useMemo } from 'react';
import { schoolService } from '../../../services';
import { SchoolStudentFilters, SchoolStudentCreateRequest, SchoolStudentUpdateRequest } from '../types';

// URL state parsers
const searchParser = parseAsString.withDefault('');
const statusParser = parseAsString.withDefault('');
const gradeParser = parseAsString.withDefault('');
const classIdParser = parseAsString.withDefault('');
const genderParser = parseAsString.withDefault('');
const pageParser = parseAsInteger.withDefault(1);
const perPageParser = parseAsInteger.withDefault(10);

const useSchoolStudent = (schoolId?: string) => {
  const queryClient = useQueryClient();

  // URL state management với nuqs
  const [search, setSearch] = useQueryState('search', searchParser);
  const [status, setStatus] = useQueryState('status', statusParser);
  const [grade, setGrade] = useQueryState('grade', gradeParser);
  const [classId, setClassId] = useQueryState('classId', classIdParser);
  const [gender, setGender] = useQueryState('gender', genderParser);
  const [page, setPage] = useQueryState('page', pageParser);
  const [per_page, setPerPage] = useQueryState('per_page', perPageParser);

  // Tạo filters object từ URL state
  const filters = useMemo(() => ({
    search: search || undefined,
    status: status || undefined,
    grade: grade || undefined,
    classId: classId || undefined,
    gender: gender || undefined,
  }), [search, status, grade, classId, gender]);

  // Query key cho caching
  const queryKey = ['school-students', schoolId, filters, page, per_page];

  // Fetch students với react-query
  const {
    data: studentsResponse,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      console.log('🔍 Fetching school students...', { schoolId, filters });
      try {
        let result;
        if (schoolId) {
          result = await schoolService.getSchoolStudents(schoolId);
        } else {
          // Load all school students if no specific schoolId
          result = await schoolService.getSchoolStudents();
        }
        console.log('✅ School students fetched successfully:', result);
        return result;
      } catch (error) {
        console.error('❌ Error fetching school students:', error);
        throw error;
      }
    },
    enabled: true, // Always enable the query
    staleTime: 5 * 60 * 1000, // 5 phút
    gcTime: 10 * 60 * 1000, // 10 phút
  });

  // Filter students based on local filters
  const filteredStudents = useMemo(() => {
    if (!studentsResponse?.data) return [];
    
    return studentsResponse.data.filter((student: any) => {
      const schoolUserData = student.schoolUserData || {};
      const metadata = schoolUserData.metadata || {};
      
      const matchesSearch = !filters.search || 
        student.name?.toLowerCase().includes(filters.search.toLowerCase()) ||
        student.email?.toLowerCase().includes(filters.search.toLowerCase()) ||
        student.code?.toLowerCase().includes(filters.search.toLowerCase()) ||
        (metadata.studentId?.toLowerCase() || '').includes(filters.search.toLowerCase()) ||
        (metadata.parentName?.toLowerCase() || '').includes(filters.search.toLowerCase());
      
      const matchesStatus = !filters.status || 
        schoolUserData.status === filters.status;
      
      const matchesGrade = !filters.grade || 
        metadata.grade === filters.grade;
      
      const matchesClass = !filters.classId || 
        student.classId === filters.classId;
      
      const matchesGender = !filters.gender || 
        metadata.gender === filters.gender;
      
      return matchesSearch && matchesStatus && matchesGrade && matchesClass && matchesGender;
    });
  }, [studentsResponse?.data, filters]);

  // Create student mutation
  const createStudentMutation = useMutation({
    mutationFn: (data: SchoolStudentCreateRequest) => {
      return schoolService.createSchoolStudent(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['school-students'] });
    },
  });

  // Update student mutation
  const updateStudentMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: SchoolStudentUpdateRequest }) => {
      return schoolService.updateSchoolStudent(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['school-students'] });
    },
  });

  // Delete student mutation
  const deleteStudentMutation = useMutation({
    mutationFn: (id: string) => {
      return schoolService.deleteSchoolStudent(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['school-students'] });
    },
  });

  // Toggle student status mutation
  const toggleStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => {
      return schoolService.toggleSchoolStudentStatus(id, status);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['school-students'] });
    },
  });

  // Filter và pagination handlers
  const updateFilters = useCallback((newFilters: Partial<SchoolStudentFilters>) => {
    if (newFilters.search !== undefined) setSearch(newFilters.search || null);
    if (newFilters.status !== undefined) setStatus(newFilters.status || null);
    if (newFilters.grade !== undefined) setGrade(newFilters.grade || null);
    if (newFilters.classId !== undefined) setClassId(newFilters.classId || null);
    if (newFilters.gender !== undefined) setGender(newFilters.gender || null);
    setPage(1);
  }, [setSearch, setStatus, setGrade, setClassId, setGender, setPage]);

  const updatePagination = useCallback((newPage: number, newPerPage?: number) => {
    setPage(newPage);
    if (newPerPage) setPerPage(newPerPage);
  }, [setPage, setPerPage]);

  const resetFilters = useCallback(() => {
    setSearch(null);
    setStatus(null);
    setGrade(null);
    setClassId(null);
    setGender(null);
    setPage(1);
  }, [setSearch, setStatus, setGrade, setClassId, setGender, setPage]);

  // Clear specific filter
  const clearFilter = useCallback((filterKey: keyof SchoolStudentFilters) => {
    switch (filterKey) {
      case 'search':
        setSearch(null);
        break;
      case 'status':
        setStatus(null);
        break;
      case 'grade':
        setGrade(null);
        break;
      case 'classId':
        setClassId(null);
        break;
      case 'gender':
        setGender(null);
        break;
    }
    setPage(1);
  }, [setSearch, setStatus, setGrade, setClassId, setGender, setPage]);

  return {
    // Data
    students: filteredStudents,
    pagination: {
      total: filteredStudents.length,
      current_page: page,
      per_page,
      total_pages: Math.ceil(filteredStudents.length / per_page)
    },
    
    // Loading states
    isLoading,
    isFetching,
    isError,
    error,

    // Current filters và pagination
    filters,
    page,
    per_page,

    // Actions
    refetch,
    updateFilters,
    updatePagination,
    resetFilters,
    clearFilter,

    // Mutations
    createStudent: createStudentMutation.mutate,
    updateStudent: updateStudentMutation.mutate,
    deleteStudent: deleteStudentMutation.mutate,
    toggleStudentStatus: toggleStatusMutation.mutate,

    // Mutation states
    isCreating: createStudentMutation.isPending,
    isUpdating: updateStudentMutation.isPending,
    isDeleting: deleteStudentMutation.isPending,
    isTogglingStatus: toggleStatusMutation.isPending,

    // Mutation objects for advanced handling
    createStudentMutation,
    updateStudentMutation,
    deleteStudentMutation,
    toggleStatusMutation,
  };
};

export default useSchoolStudent;
